import { Form, FormInstance } from 'antd';
import { DrawerProps } from 'antd/lib/drawer';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Step, Steps, Wizard, WizardContext } from 'react-albus';
import { ProjectId } from 'api/models';
import { ProjectBulkMergeResult } from 'api/project';
import { OzoneDrawer } from 'components/atoms/drawer';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import {
  useProjectBulkMergeMutation,
  useProjectBulkMergePreviewMutation,
} from 'queries/project';
import { Footer } from './drawer-footer';
import { Title } from './drawer-title';
import { StepId, StepIds, StepOne, StepTwo } from './steps';
import { ImportFromTemplateFormValues, ImportTitle } from './types';
import { toProjectBulkMergePayload } from './utils';

type Props = Pick<DrawerProps, 'visible'> &
  ImportTitle & {
    projectId: ProjectId;
    onClose: () => void;
  };

interface UseOnNextParam {
  projectId: ProjectId;
  onFinish: () => void;
}

type UseOnNext = (params: UseOnNextParam) => {
  onNext: (wizard: WizardContext) => void;
  form: FormInstance;
  previewResult?: ProjectBulkMergeResult[];
  isLoading: boolean;
};

const useOnNext: UseOnNext = ({ projectId, onFinish }) => {
  const successTitle = useTranslation('success');
  const successMessage = useTranslation(
    'projects.phase.import.success.message',
  );

  const [previewResult, setPreviewResult] = useState<ProjectBulkMergeResult[]>(
    [],
  );
  const [form] = Form.useForm<ImportFromTemplateFormValues>();
  const [formValues, setFormValues] = useState<ImportFromTemplateFormValues>({
    sources: [],
  });

  const { mutateAsync: preview, isLoading: isGettingPreviewResult } =
    useProjectBulkMergePreviewMutation(projectId);
  const { mutateAsync: merge, isLoading } =
    useProjectBulkMergeMutation(projectId);

  const onNext = useCallback(
    async (context) => {
      const { step, push } = context;
      const id = step.id as StepId;

      switch (id) {
        case 'Step1':
          form.validateFields().then((values) => {
            setFormValues(values);
            preview(toProjectBulkMergePayload(values), {
              onSuccess: (result) => {
                setPreviewResult(result);
                push();
              },
            });
          });

          break;
        case 'Step2':
          await merge(toProjectBulkMergePayload(formValues), {
            onSuccess: async () => {
              toastService({
                title: successTitle,
                message: successMessage,
              }).success();
              onFinish();
            },
          });

          break;
        default:
      }
    },
    [form, preview, merge, formValues, successTitle, successMessage, onFinish],
  );

  return useMemo(
    () => ({
      onNext,
      form,
      previewResult,
      isLoading: isGettingPreviewResult || isLoading,
    }),
    [onNext, form, previewResult, isLoading, isGettingPreviewResult],
  );
};

export const ImportFromTemplate: FC<Props> = ({
  visible,
  onClose,
  projectId,
  title,
}) => {
  const { form, onNext, isLoading, previewResult } = useOnNext({
    projectId,
    onFinish: onClose,
  });

  return (
    <Wizard onNext={onNext}>
      <OzoneDrawer
        width={960}
        visible={visible}
        onClose={onClose}
        title={<Title title={title} />}
        footer={<Footer isLoading={isLoading} onClose={onClose} />}
      >
        <Steps>
          <Step id={StepIds.Step1}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ sources: [{ prefix: '' }] }}
            >
              <StepOne />
            </Form>
          </Step>
          <Step id={StepIds.Step2}>
            <StepTwo data={previewResult} />
          </Step>
        </Steps>
      </OzoneDrawer>
    </Wizard>
  );
};
